import { useTheme } from '@mui/material/styles'
import { LineChart, Line, XAxis, YAxis } from 'recharts'

interface ECGChartProps {
  data: Array<{ time: number; voltage: number }>
  maxTimeInMm: number
  strokeWidth?: number
  margin?: { top: number; right: number; left: number; bottom: number }
  voltageRangeMv?: number // Range mínimo de voltagem em mV (padrão: 2mV = ±1mV)
  voltageThresholdMv?: number // Padding extra em mV para modo dinâmico
  heightInLargeSquares?: number // Altura fixa em quadrados grandes (1 quadrado = 25px = 0.5mV)
}

/**
 * Componente de gráfico ECG reutilizável
 * Renderiza onda de ECG com configurações padronizadas
 *
 * Escala fixa:
 * - Horizontal: 25mm/s = 125px/s (5px por mm)
 * - Vertical: 1mV = 10mm = 50px (5px por mm)
 */
/**
 * Gráfico de ECG reutilizável com escala fixa:
 * - Horizontal: 25mm/s = 125px/s (5px por mm)
 * - Vertical: 1mV = 10mm = 50px (5px por mm)
 *
 * heightInLargeSquares: fixa a altura em quadrados grandes (1 quadrado = 0.5mV)
 * Caso não informado, a altura é dinâmica conforme o range dos dados.
 */
export const ECGChart = ({
  data,
  maxTimeInMm,
  strokeWidth = 0.7,
  margin = { top: 10, right: 0, left: 0, bottom: 10 },
  voltageRangeMv = 2,
  voltageThresholdMv = 2,
  heightInLargeSquares
}: ECGChartProps) => {
  const theme = useTheme()
  // Largura do gráfico: 5px por mm
  const chartWidthPx = maxTimeInMm * 5

  let chartHeightPx: number
  let minVoltageInMv: number
  let maxVoltageInMv: number

  const dataVoltages = data.map(d => d.voltage)
  const minDataVoltage = Math.min(...dataVoltages)
  const maxDataVoltage = Math.max(...dataVoltages)

  const maxAbsVoltage = Math.max(
    Math.abs(minDataVoltage),
    Math.abs(maxDataVoltage)
  )

  if (heightInLargeSquares === undefined) {
    const calculatedRangeMv = Math.max(
      maxAbsVoltage + voltageThresholdMv,
      voltageRangeMv
    )

    // Altura baseada no range calculado
    // 1mV = 10mm = 50px (sempre, independente da duração)
    chartHeightPx = calculatedRangeMv * 2 * 50 // Ex: ±1.5mV (3mV total) = 3 * 50 = 150px

    // Domínios simétricos para manter o zero no centro
    minVoltageInMv = -calculatedRangeMv
    maxVoltageInMv = calculatedRangeMv
  } else {
    chartHeightPx = heightInLargeSquares * 25 // Ex: 4 quadrados = 100px, 8 quadrados = 200px

    // O domínio Y deve ser sempre simétrico e fixo, cobrindo heightInLargeSquares * 0.5mV
    // Ex: 8 quadrados grandes = 4mV total, ±2mV
    const mvHalfRange = (heightInLargeSquares * 0.5) / 2
    minVoltageInMv = -mvHalfRange
    maxVoltageInMv = mvHalfRange
  }

  return (
    <LineChart
      width={chartWidthPx}
      height={chartHeightPx}
      data={data}
      margin={margin}
      style={{ position: 'absolute', top: 0, left: 0 }}
    >
      <XAxis hide dataKey='time' type='number' domain={[0, maxTimeInMm]} />
      <YAxis
        hide
        domain={[minVoltageInMv, maxVoltageInMv]}
        scale='linear'
        allowDataOverflow={true}
        type='number'
      />
      <Line
        type='monotone'
        dataKey='voltage'
        stroke={theme.palette.text.primary}
        dot={false}
        strokeWidth={strokeWidth}
        isAnimationActive={false}
      />
    </LineChart>
  )
}
